import type { ReactNode, InputHTMLAttributes, SelectHTMLAttributes } from 'react';
import Swal from 'sweetalert2';
import { statusLabel, verdictLabel, severityLabel } from '@aiinterview/shared';

/**
 * The shared kit. Every one of these replaces a Bootstrap pattern that was repeated across the
 * Razor views, so the pages that follow assemble rather than reinvent.
 */

/**
 * The page's own heading, above the content and outside any card.
 *
 * Every screen gets exactly one, and it is the largest text on it. Without this the only heading a
 * page had was its card's title, which renders at the same size as the topbar breadcrumb — so the
 * page name appeared twice, identically, and nothing said what you were looking at.
 */
export function PageHeader({ title, lede, action }: {
    title: string;
    /** One line saying what the page is for. Every list screen in the original had one. */
    lede?: string;
    action?: ReactNode;
}) {
    return (
        <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
            <div>
                <h1 className="app-page-heading">{title}</h1>
                {lede && <p className="app-page-lede max-w-[75ch]">{lede}</p>}
            </div>
            {action}
        </header>
    );
}

export function Card({ title, lede, action, heading = false, children }: {
    title?: string;
    /** One line saying what the page is for. Every list screen in the original had one. */
    lede?: string;
    action?: ReactNode;
    /**
     * This card's title is the page's title, so it is promoted out of the card's header bar and
     * rendered above it as the page heading.
     *
     * Most screens are a single card whose title names the whole page. Left inside the card that
     * title sat at the same size as the topbar breadcrumb, so the page said its own name twice and
     * had no heading. Setting this is what gives the page one.
     */
    heading?: boolean;
    children: ReactNode;
}) {
    const card = (
        <section className="app-card">
            {!heading && (title || action) && (
                <header className="app-card-header">
                    <div>
                        {title && <h2 className="app-card-title">{title}</h2>}
                        {lede && <p className="app-card-lede">{lede}</p>}
                    </div>
                    {action}
                </header>
            )}
            <div className="app-card-body">{children}</div>
        </section>
    );

    if (!heading) return card;

    return (
        <>
            <PageHeader title={title ?? ''} lede={lede} action={action} />
            {card}
        </>
    );
}

export function Button({ variant = 'primary', busy, children, ...rest }: {
    /** danger-quiet is a red outline: destructive, but sitting in a row of ordinary actions. */
    variant?: 'primary' | 'secondary' | 'danger' | 'danger-quiet';
    busy?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement>) {
    const styles = {
        primary: 'app-btn-primary',
        secondary: 'app-btn-secondary',
        danger: 'app-btn-danger',
        'danger-quiet': 'app-btn-danger-quiet'
    }[variant];

    return (
        <button
            {...rest}
            // Guards the double-submit the Razor pages had to defend against in script. The server
            // refuses a second submission too; this stops the user seeing an error for it.
            disabled={rest.disabled || busy}
            className={`app-btn ${styles} ${rest.className ?? ''}`}
        >
            {busy && <Spinner />}
            {children}
        </button>
    );
}

const Spinner = () => (
    <span
        aria-hidden="true"
        className="size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
    />
);

export function Field({ label, error, hint, required, children }: {
    label: string;
    error?: string;
    hint?: string;
    /**
     * Draws the asterisk and says "required" to a screen reader, which cannot see one. It marks the
     * field and nothing more - the rule itself lives with the form that owns it, so a field is never
     * starred here and optional there.
     */
    required?: boolean;
    children: ReactNode;
}) {
    return (
        <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-700">
                {label}
                {required && (
                    <>
                        <span aria-hidden="true" className="ml-0.5 text-red-600">*</span>
                        <span className="sr-only"> (required)</span>
                    </>
                )}
            </span>
            {children}
            {hint && !error && <span className="mt-1 block text-xs text-slate-500">{hint}</span>}
            {error && (
                <span role="alert" className="mt-1 block text-xs font-medium text-red-600">
                    {error}
                </span>
            )}
        </label>
    );
}

/**
 * One class for every control, defined in theme.css from the original's form styling. A caller's own
 * className is appended rather than replacing it, so a width utility does not cost the field its
 * border and focus ring.
 */
const inputClass = (extra?: string) => `app-input ${extra ?? ''}`.trim();

export const TextInput = ({ invalid, className, ...rest }:
    { invalid?: boolean } & InputHTMLAttributes<HTMLInputElement>) => (
    <input {...rest} aria-invalid={invalid} className={inputClass(className)} />
);

export const TextArea = ({ invalid, className, ...rest }:
    { invalid?: boolean } & React.TextareaHTMLAttributes<HTMLTextAreaElement>) => (
    <textarea {...rest} aria-invalid={invalid} rows={rest.rows ?? 3} className={inputClass(className)} />
);

export const Select = ({ invalid, className, ...rest }:
    { invalid?: boolean } & SelectHTMLAttributes<HTMLSelectElement>) => (
    <select {...rest} aria-invalid={invalid} className={inputClass(className)} />
);

export function Checkbox({ label, hint, checked, onChange }: {
    label: string;
    hint?: string;
    checked: boolean;
    onChange: (checked: boolean) => void;
}) {
    return (
        <label className="flex items-start gap-2.5">
            <input
                type="checkbox"
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
                className="mt-0.5 size-4 rounded border-slate-300 accent-[var(--brand-primary)]"
            />
            <span>
                <span className="block text-sm text-slate-700">{label}</span>
                {hint && <span className="block text-xs text-slate-500">{hint}</span>}
            </span>
        </label>
    );
}

/**
 * Status, verdict and severity always read as words.
 *
 * The labels come from the shared package, the same module the API uses. That is what stops the
 * front end growing a second, drifting vocabulary - the thing plainLanguage exists to prevent.
 */
export function StatusPill({ kind, value }: {
    kind: 'status' | 'review' | 'verdict' | 'severity';
    value: string | number | null;
}) {
    if (value === null || value === '') {
        return <span className="text-xs text-slate-400">—</span>;
    }

    const text = kind === 'status' ? statusLabel(String(value))
        : kind === 'verdict' ? verdictLabel(String(value))
            : kind === 'severity' ? severityLabel(Number(value))
                : String(value);

    const tone = toneFor(kind, value);

    return (
        <span className={`status-pill ${tone}`}>
            {text}
        </span>
    );
}

/**
 * The original had exactly four pill palettes, defined in app.css. Reusing them rather than picking
 * fresh Tailwind shades per kind is what keeps a status looking the same on the dashboard, the list
 * and the report.
 */
function toneFor(kind: string, value: string | number): string {
    const key = String(value);

    if (kind === 'verdict') {
        return {
            Clean: 'is-completed',
            Minor: 'is-awaiting',
            Suspicious: 'is-awaiting',
            HighRisk: 'is-bad'
        }[key] ?? 'is-pending';
    }

    if (kind === 'review') {
        return { Approved: 'is-completed', Rejected: 'is-bad' }[key] ?? 'is-pending';
    }

    if (kind === 'severity') {
        return Number(value) >= 3 ? 'is-bad' : Number(value) === 2 ? 'is-awaiting' : 'is-pending';
    }

    return {
        Completed: 'is-completed',
        InProgress: 'is-progress',
        Processing: 'is-progress',
        Cancelled: 'is-pending',
        Expired: 'is-pending',
        Pending: 'is-pending'
    }[key] ?? 'is-pending';
}

export const EmptyState = ({ message }: { message: string }) => (
    <p className="py-8 text-center text-sm text-slate-500">{message}</p>
);

export const ErrorNote = ({ message }: { message: string }) => (
    <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{message}</p>
);

export const Loading = ({ what = 'Loading' }: { what?: string }) => (
    <p className="py-8 text-center text-sm text-slate-500">{what}…</p>
);

/** The confirm dialog, with the corrected button wording carried over from the Razor fix. */
export async function confirmAction(options: {
    title: string;
    text?: string;
    confirmText: string;
    danger?: boolean;
}): Promise<boolean> {
    const result = await Swal.fire({
        title: options.title,
        text: options.text,
        icon: options.danger ? 'warning' : 'question',
        showCancelButton: true,
        // Named for what the button does, never "OK". A recruiter should be able to read the button
        // and know what is about to happen.
        confirmButtonText: options.confirmText,
        cancelButtonText: 'Cancel',
        confirmButtonColor: options.danger ? '#dc2626' : '#1B84FF',
        reverseButtons: true
    });

    return result.isConfirmed;
}

export const toast = (message: string, kind: 'success' | 'error' = 'success') =>
    Swal.fire({
        text: message,
        icon: kind,
        toast: true,
        position: 'top-end',
        timer: kind === 'error' ? 6000 : 3000,
        timerProgressBar: true,
        showConfirmButton: false
    });
